import React, { useEffect, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Field, TextInput, TextArea } from "./shared";

const EMPTY_ROW = { shape_id: "", colour_id: "", text_line1: "", text_line2: "", size: "", quantity: 1 };
const newRow = (shapeId = "", colourId = "") => ({ ...EMPTY_ROW, uid: crypto.randomUUID(), shape_id: shapeId, colour_id: colourId });

export const ManualOrderForm = ({ onCreated, onClose }) => {
  const [shapes, setShapes] = useState([]);
  const [colours, setColours] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", status: "new", notes: "", total_override: "" });
  const [rows, setRows] = useState([newRow()]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/admin/shapes"), api.get("/admin/colours")]).then(([s, c]) => {
      const sh = s.data.filter((x) => x.active);
      const co = c.data.filter((x) => x.active);
      setShapes(sh);
      setColours(co);
      setRows([newRow(sh[0]?.id || "", co[0]?.id || "")]);
    });
  }, []);

  const setRow = (i, patch) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/orders", {
        customer: { name: form.name, email: form.email, phone: form.phone, address: form.address },
        items: rows.map(({ uid, ...r }) => ({ ...r, quantity: Number(r.quantity) || 1 })),
        status: form.status,
        notes: form.notes,
        total_override: form.total_override === "" ? null : Number(form.total_override),
      });
      toast.success(`Order ${data.order_number} created${form.status === "new" ? " — sent to print queue" : ""}`);
      onCreated(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create order");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-warmwhite border-2 border-plum/30 rounded-[1.5rem] p-6 mb-6 space-y-4" data-testid="manual-order-form">
      <div className="flex items-center justify-between">
        <h2 className="font-serif-display uppercase text-plum-deep text-xl">NEW MANUAL ORDER / QUOTE</h2>
        <button type="button" onClick={onClose} className="text-plum-deep/40 hover:text-plum-deep" aria-label="Close"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="CUSTOMER NAME *"><TextInput required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="manual-customer-name" /></Field>
        <Field label="EMAIL"><TextInput type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="manual-customer-email" /></Field>
        <Field label="PHONE"><TextInput value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
        <Field label="ADDRESS"><TextInput value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
      </div>

      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-plum-deep/60 mb-2 font-semibold">TAGS</p>
        <div className="space-y-2">
          {rows.map((row, i) => (
            <div key={row.uid || i} className="flex flex-wrap items-center gap-2 bg-cream rounded-2xl p-2.5">
              <select value={row.shape_id} onChange={(e) => setRow(i, { shape_id: e.target.value })} data-testid={`manual-item-shape-${i}`} className="rounded-full px-3 py-2 bg-warmwhite border border-plum-deep/15 text-xs focus:outline-none">
                {shapes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              {shapes.find((s) => s.id === row.shape_id)?.key === "etchery_plant_stake" && (
                <select value={row.size || "20cm"} onChange={(e) => setRow(i, { size: e.target.value })} data-testid={`manual-item-size-${i}`} className="rounded-full px-3 py-2 bg-warmwhite border border-plum-deep/15 text-xs focus:outline-none">
                  <option value="15cm">15CM</option>
                  <option value="20cm">20CM</option>
                  <option value="25cm">25CM</option>
                </select>
              )}
              <select value={row.colour_id} onChange={(e) => setRow(i, { colour_id: e.target.value })} data-testid={`manual-item-colour-${i}`} className="rounded-full px-3 py-2 bg-warmwhite border border-plum-deep/15 text-xs focus:outline-none">
                {colours.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <TextInput placeholder="Line 1" value={row.text_line1} onChange={(e) => setRow(i, { text_line1: e.target.value })} data-testid={`manual-item-text1-${i}`} className="flex-1 min-w-[120px] !py-2 text-xs" />
              <TextInput placeholder="Line 2" value={row.text_line2} onChange={(e) => setRow(i, { text_line2: e.target.value })} className="flex-1 min-w-[100px] !py-2 text-xs" />
              <TextInput type="number" min={1} value={row.quantity} onChange={(e) => setRow(i, { quantity: e.target.value })} data-testid={`manual-item-qty-${i}`} className="w-20 !py-2 text-xs" />
              {rows.length > 1 && (
                <button type="button" onClick={() => setRows((r) => r.filter((_, j) => j !== i))} className="text-plum-deep/40 hover:text-red-600" aria-label="Remove row"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          ))}
        </div>
        <button type="button" onClick={() => setRows((r) => [...r, newRow(shapes[0]?.id || "", colours[0]?.id || "")])} data-testid="manual-add-item" className="mt-3 inline-flex items-center gap-2 text-magenta text-xs uppercase tracking-[0.14em] font-semibold hover:underline">
          <Plus className="w-3.5 h-3.5" /> ADD ANOTHER TAG
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="START AS">
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="manual-status" className="w-full rounded-full px-5 py-2.5 bg-cream border border-plum-deep/15 text-sm focus:outline-none focus:border-magenta">
            <option value="draft">DRAFT QUOTE</option>
            <option value="awaiting_payment">AWAITING PAYMENT</option>
            <option value="new">NEW (SEND TO PRINT QUEUE)</option>
          </select>
        </Field>
        <Field label="TOTAL OVERRIDE (NZD, OPTIONAL)">
          <TextInput type="number" step="0.5" value={form.total_override} onChange={(e) => setForm({ ...form, total_override: e.target.value })} data-testid="manual-total-override" placeholder="Auto-priced if empty" />
        </Field>
        <Field label="NOTES"><TextInput value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
      </div>

      <button type="submit" disabled={busy} data-testid="manual-create-order" className="inline-flex items-center gap-2 rounded-full bg-plum text-cream px-7 py-3 text-xs uppercase tracking-[0.16em] font-semibold hover:bg-magenta disabled:opacity-50">
        {busy ? "CREATING…" : "CREATE ORDER"}
      </button>
    </form>
  );
};
